import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Moon, Sun, Tag, Search, Loader2 } from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { dropList } from "./dropContent";

const DropListPage = ({ nightMode, setNightMode }) => {
  const navigate = useNavigate();

  const focusOptions = useMemo(() => {
    const values = new Set(dropList.map((drop) => drop.metadata?.theme).filter(Boolean));
    return ["All", ...Array.from(values)];
  }, []);

  const tagOptions = useMemo(() => {
    const values = new Set();
    dropList.forEach((drop) => {
      drop.metadata?.tags?.forEach((tag) => values.add(tag));
    });
    return ["All", ...Array.from(values)];
  }, []);

  const [focusFilter, setFocusFilter] = useState("All");
  const [tagFilter, setTagFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [embedder, setEmbedder] = useState(null);
  const [articleEmbeddings, setArticleEmbeddings] = useState([]);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);

  // Initialize semantic search model
  useEffect(() => {
    const initModel = async () => {
      try {
        // Use a simpler approach - convert to vectors on the fly
        setEmbedder(true); // Just mark as ready

        // Pre-compute simple keyword vectors for articles
        const embeddings = dropList.map((drop) => {
          const text = [
            drop.hero.title,
            drop.hero.subtitle,
            drop.excerpt,
            drop.metadata?.tags?.join(' '),
            drop.metadata?.theme,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();

          return {
            drop,
            text,
            keywords: text.split(/\s+/).filter(word => word.length > 2),
          };
        });
        setArticleEmbeddings(embeddings);
      } catch (error) {
        console.error('Failed to initialize semantic search:', error);
      }
    };
    initModel();
  }, []);

  // Calculate semantic similarity
  const calculateSimilarity = useCallback((queryText, articleData) => {
    const queryTerms = queryText.toLowerCase().split(/\s+/).filter(word => word.length > 1);
    const articleText = articleData.text;

    if (queryTerms.length === 0) return 0;

    let matchScore = 0;
    let maxPossibleScore = queryTerms.length * 10;

    queryTerms.forEach(term => {
      // Check if term exists in article text
      if (articleText.includes(term)) {
        matchScore += 10; // Full match
      } else {
        // Check for partial matches
        const words = articleText.split(/\s+/);
        words.forEach(word => {
          if (word.includes(term) && word.length > 3) {
            matchScore += 5; // Partial match
          } else if (term.includes(word) && term.length > 3) {
            matchScore += 3; // Contains word
          }
        });
      }
    });

    return matchScore / maxPossibleScore;
  }, []);

  // Semantic search effect
  useEffect(() => {
    const performSearch = () => {
      if (!searchQuery.trim() || !embedder || articleEmbeddings.length === 0) {
        setSearchResults([]);
        setShowDropdown(false);
        return;
      }

      setIsSearching(true);
      try {
        console.log('Search query:', searchQuery);
        console.log('Article embeddings:', articleEmbeddings.length);

        const results = articleEmbeddings
          .map((item) => {
            const similarity = calculateSimilarity(searchQuery, item);
            console.log(`Article: ${item.drop.hero.title}, Similarity: ${similarity}`);
            return {
              ...item.drop,
              similarity,
            };
          })
          .filter((item) => item.similarity > 0.05)
          .sort((a, b) => b.similarity - a.similarity)
          .slice(0, 5);

        console.log('Search results:', results);
        setSearchResults(results);
        setShowDropdown(results.length > 0);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setIsSearching(false);
      }
    };

    const debounce = setTimeout(performSearch, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, embedder, articleEmbeddings, calculateSimilarity]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        searchRef.current &&
        !searchRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredDrops = useMemo(() => {
    let results = dropList;

    // Apply category filters
    results = results.filter((drop) => {
      const focusMatch = focusFilter === "All" || drop.metadata?.theme === focusFilter;
      const tagMatch =
        tagFilter === "All" || drop.metadata?.tags?.some((tag) => tag === tagFilter);
      return focusMatch && tagMatch;
    });

    return results;
  }, [focusFilter, tagFilter]);

  return (
    <div className={`drop-index ${nightMode ? "night" : ""}`}>
      <div className="drop-index-backdrop" />
      <Navigation activeSection="drop" onNavigate={() => {}} nightMode={nightMode} />

      <main className="drop-index-main">
        <header className="drop-index-hero">
          <motion.div
            className="drop-index-toprow"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <button
              className="drop-index-back"
              type="button"
              onClick={() => navigate('/')}
              aria-label="Back to home"
            >
              <span aria-hidden="true">←</span>
              <span>Back</span>
            </button>
            <button className="drop-index-toggle" onClick={() => setNightMode(!nightMode)}>
              {nightMode ? <Sun size={16} /> : <Moon size={16} />}
              <span>{nightMode ? "Day" : "Night"} mode</span>
            </button>
          </motion.div>

          <motion.span
            className="drop-index-label"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
          >
            MY BLOGS
          </motion.span>

          <motion.h1
            className="drop-index-title"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.1 }}
          >
            Where my <span className="drop-title-gradient">thoughts</span> find<br />their shape.
          </motion.h1>

          <motion.p
            className="drop-index-subtitle"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.12 }}
          >
            A running archive of things that make me think.
          </motion.p>

          <motion.div
            className="drop-search-container"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.16 }}
            ref={searchRef}
          >
            <Search className="drop-search-icon" size={18} />
            <input
              type="text"
              className="drop-search-input"
              placeholder="Semantic search: articles, topics, insights..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
              aria-label="Search articles"
            />
            {isSearching && (
              <Loader2 className="drop-search-loading" size={18} />
            )}
            {searchQuery && !isSearching && (
              <button
                className="drop-search-clear"
                onClick={() => {
                  setSearchQuery("");
                  setShowDropdown(false);
                }}
                aria-label="Clear search"
              >
                ✕
              </button>
            )}

            <AnimatePresence>
              {showDropdown && searchResults.length > 0 && (
                <motion.div
                  ref={dropdownRef}
                  className="drop-search-dropdown"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {searchResults.map((result) => (
                    <div
                      key={result.slug}
                      className="drop-search-result"
                      onClick={() => {
                        if (!result.locked) {
                          navigate(`/drop/${result.slug}`);
                        }
                      }}
                    >
                      <div className="drop-search-result-content">
                        <h3 className="drop-search-result-title">
                          {result.hero.title}
                        </h3>
                        {result.excerpt && (
                          <p className="drop-search-result-excerpt">
                            {result.excerpt.slice(0, 100)}...
                          </p>
                        )}
                        <div className="drop-search-result-meta">
                          {result.metadata?.tags?.slice(0, 2).map((tag) => (
                            <span key={tag} className="drop-search-result-tag">
                              {tag}
                            </span>
                          ))}
                          {result.similarity && (
                            <span className="drop-search-result-score">
                              {Math.round(result.similarity * 100)}% match
                            </span>
                          )}
                        </div>
                      </div>
                      {result.locked && (
                        <span className="drop-search-result-locked">Locked</span>
                      )}
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          <motion.div
            className="drop-index-filters"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="drop-filter-group">
              <span className="drop-filter-label">Industry</span>
              <select
                className="drop-filter-select"
                value={focusFilter}
                onChange={(evt) => setFocusFilter(evt.target.value)}
              >
                {focusOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div className="drop-filter-group">
              <span className="drop-filter-label">Deliverable</span>
              <select
                className="drop-filter-select"
                value={tagFilter}
                onChange={(evt) => setTagFilter(evt.target.value)}
              >
                {tagOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </motion.div>
        </header>

        <section className="drop-card-grid">
          {filteredDrops.map((drop) => {
            const isLocked = drop.locked;
            const handleNavigate = () => {
              if (!isLocked) {
                navigate(`/drop/${drop.slug}`);
              }
            };
            return (
              <motion.article
                key={drop.slug}
                className={`drop-card ${isLocked ? 'drop-card--locked' : ''}`}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55 }}
                whileHover={isLocked ? {} : {
                  y: -8,
                  scale: 1.02,
                  transition: { duration: 0.4, ease: [0.4, 0, 0.2, 1] }
                }}
                onClick={handleNavigate}
              >
                <div className="drop-card-image">
                  {drop.coverImage ? (
                    <img src={drop.coverImage} alt={drop.hero.title} loading="lazy" />
                  ) : (
                    <div className="drop-card-fallback" aria-hidden="true" />
                  )}
                </div>
                <div className="drop-card-info">
                  <h2 className="drop-card-title">{drop.hero.title}</h2>
                  {drop.metadata?.tags?.length ? (
                    <div className="drop-card-tags">
                      {drop.metadata.tags.map((tag) => (
                        <span key={tag}>{tag}</span>
                      ))}
                    </div>
                  ) : null}
                </div>
                {isLocked && (
                  <div className="drop-card-locked-overlay">
                    <span className="drop-card-locked-text">Coming Soon</span>
                  </div>
                )}
              </motion.article>
            );
          })}
        </section>
      </main>

      <Footer nightMode={nightMode} />
    </div>
  );
};

export default DropListPage;
